import { Link } from 'react-router-dom';
import { useStateContext } from '../../hooks/useStateContext';
import useSEO from '../../hooks/useSEO';
import { schemaGraph } from '../../utils/schema';

// State-scoped homepage — currently only used for launched states.
// Unlaunched states render StateComingSoonPage instead (handled in App routes).
export default function StateHomePage() {
  const { state } = useStateContext();

  useSEO({
    title: `${state.name} Real Estate CE Online | Steady Education`,
    description: `${state.regulator}-approved online continuing education for ${state.name} real estate licensees. All ${state.requiredHours} required hours for $${state.packagePrice}. Self-paced, instant certificates.`,
    url: `https://steadyeducation.com/${state.slug}`,
    jsonLd: schemaGraph({
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: state.name, url: `https://steadyeducation.com/${state.slug}` },
      ],
    }),
  });

  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <div className="container">
          <div style={styles.regBadge}>{state.regulator}-approved · School #{state.schoolLicense}</div>
          <h1 style={styles.heroTitle}>{state.name} Real Estate Continuing Education</h1>
          <p style={styles.heroSubtitle}>
            All {state.requiredHours} required CE hours online — self-paced, {state.regulator}-approved,
            {' '}instant certificates. Complete package: <strong>${state.packagePrice}</strong>.
          </p>
          <div style={styles.ctaRow}>
            <Link to={`/${state.slug}/courses`} className="btn btn-primary btn-lg">Browse Courses →</Link>
            {state.hasGuides && (
              <Link to={`/${state.slug}/resources`} className="btn btn-secondary btn-lg">Read the Guides</Link>
            )}
            {/* States with one guide rather than a hub link it directly, so it isn't orphaned. */}
            {!state.hasGuides && state.pillarGuide && (
              <Link to={state.pillarGuide} className="btn btn-secondary btn-lg">CE Requirements Guide</Link>
            )}
          </div>
        </div>
      </div>

      {/* Value props */}
      <div className="container" style={styles.valueGrid}>
        {[
          { emoji: '⚡', title: 'Instant certificates', desc: `Certificate issued the moment you pass — submit to ${state.regulator} the same day.` },
          { emoji: '📱', title: '100% online, self-paced', desc: 'Any device, any time. Progress saves automatically.' },
          { emoji: '✅', title: `Every ${state.regulator} category`, desc: `Package covers all ${state.requiredHours} required hours across every mandatory topic.` },
          { emoji: '💰', title: 'Best price in the state', desc: `Complete package at $${state.packagePrice} — beats every major competitor.` },
        ].map(v => (
          <div key={v.title} style={styles.valueCard}>
            <div style={styles.valueIcon}>{v.emoji}</div>
            <div style={styles.valueTitle}>{v.title}</div>
            <div style={styles.valueDesc}>{v.desc}</div>
          </div>
        ))}
      </div>

      {/* Package callout */}
      <div className="container" style={{ marginTop: 32 }}>
        <div style={styles.packageCard}>
          <div>
            <div style={styles.packageTag}>⭐ Best Value</div>
            <h2 style={styles.packageTitle}>Complete {state.name} CE Package</h2>
            <p style={styles.packageDesc}>
              Every {state.regulator}-required hour, packaged together — the fastest way to renew your {state.name} license.
            </p>
            <div style={styles.packageFeatures}>
              <span>✓ All {state.requiredHours} required hours</span>
              <span>✓ Every mandatory category</span>
              <span>✓ Instant certificates</span>
              <span>✓ Self-paced</span>
            </div>
          </div>
          <div style={styles.packageRight}>
            <div style={styles.packagePrice}>${state.packagePrice}</div>
            <Link to={`/${state.slug}/courses`} style={styles.packageBtn}>Get the package →</Link>
            <div style={styles.secureNote}>🔒 Secure checkout via Stripe</div>
          </div>
        </div>
      </div>

      {/* Trust footer */}
      <div className="container" style={styles.trustBox}>
        <div style={{ fontSize: 24 }}>🏛️</div>
        <div>
          <strong>Approved by the {state.regulatorFull}</strong>
          <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
            School License {state.schoolLicense}{state.regulatorUrl && (
              <> · <a href={state.regulatorUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>Verify with {state.regulator}</a></>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { background: '#FAF7F2', minHeight: '100vh' },
  hero: { background: 'linear-gradient(135deg, #2C453E 0%, #243A34 100%)', color: 'white', padding: '56px 0 48px' },
  regBadge: { display: 'inline-block', background: 'rgba(182,161,137,0.2)', color: '#d4b896', fontSize: 12, fontWeight: 700, padding: '5px 14px', borderRadius: 999, marginBottom: 16, border: '1px solid rgba(182,161,137,0.4)' },
  heroTitle: { fontFamily: 'var(--font-display)', fontSize: 'clamp(1.9rem, 4vw, 2.8rem)', color: 'white', marginBottom: 14, lineHeight: 1.15 },
  heroSubtitle: { fontSize: 17, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, maxWidth: 720, marginBottom: 28 },
  ctaRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  valueGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16, marginTop: 40 },
  valueCard: { background: 'white', border: '1px solid #e8e2d6', borderRadius: 14, padding: 24 },
  valueIcon: { fontSize: 32, marginBottom: 10 },
  valueTitle: { fontFamily: 'var(--font-display)', fontSize: 17, color: '#243A34', marginBottom: 6 },
  valueDesc: { fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.55 },
  packageCard: { background: 'linear-gradient(135deg, #2C453E, #243A34)', color: 'white', borderRadius: 20, padding: '36px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' },
  packageTag: { display: 'inline-block', background: 'rgba(182,161,137,0.25)', color: '#d4b896', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999, marginBottom: 10, border: '1px solid rgba(182,161,137,0.4)' },
  packageTitle: { fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 10 },
  packageDesc: { fontSize: 14, color: 'rgba(255,255,255,0.8)', marginBottom: 16, maxWidth: 480 },
  packageFeatures: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: 'rgba(255,255,255,0.85)' },
  packageRight: { textAlign: 'center', minWidth: 200 },
  packagePrice: { fontSize: 48, fontWeight: 800, lineHeight: 1, marginBottom: 12 },
  packageBtn: { display: 'inline-block', background: '#B6A189', color: 'white', textDecoration: 'none', fontWeight: 700, padding: '12px 24px', borderRadius: 10, fontSize: 15 },
  secureNote: { fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 10 },
  trustBox: { display: 'flex', alignItems: 'flex-start', gap: 14, background: 'var(--tan-50)', border: '1px solid #b39e89', borderRadius: 12, padding: '16px 20px', marginTop: 40, marginBottom: 40 },
};
